import { JSX, useEffect, useState } from 'react';

import { FileObject } from '../types';

type FileState = {
  error: string;
  file: File | null;
};

interface S3FileUploaderProps {
  onComplete: () => Promise<void>;
  children: (props: {
    filesState: {
      [key: string]: FileState;
    };
    isUploading: boolean;
    uploadFiles: () => Promise<void>;
    retryUpload: () => Promise<void>;
    onFileChange: (key: string, file: File) => void;
  }) => JSX.Element;
  config: {
    key: string;
    uploadUrl: string;
    fileUrl: string;
    fileName?: string;
    fileObject?: FileObject;
  }[];
  onError?: (fileKey: string) => void;
  refetchCredentials: () => Promise<void>;
  skipUpload?: boolean;
}

export function S3FileUploader({
  children,
  config,
  onError,
  onComplete,
  refetchCredentials,
  skipUpload = false,
}: S3FileUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isFilesUploaded, setIsFilesUploaded] = useState(false);
  const [filesState, setFilesState] = useState<{ [key: string]: FileState }>(
    {}
  );

  const prefetchFiles = async () => {
    try {
      setIsUploading(true);
      const apiFilesConfig = config.filter((item) => !item.fileObject);
      const preCacheFilesConfig = config.filter((item) => item.fileObject);

      if (apiFilesConfig.length > 0) {
        await Promise.all(
          apiFilesConfig.map(async (item) => {
            if (item.fileUrl) {
              const response = await fetch(item.fileUrl);

              if (!response.ok || !item.fileUrl) {
                onError?.(item.key);
                return;
              }

              const blob = await response.blob();

              const fileName =
                item.fileName || item.fileUrl?.split('/')?.pop() || '';

              const fileObject = new File([blob], fileName, {
                type: blob.type,
              });

              setFilesState((prev) => ({
                ...prev,
                [item.key]: {
                  file: fileObject,
                  error: '',
                },
              }));
            }
          })
        );
      }

      if (preCacheFilesConfig.length > 0) {
        await Promise.all(
          preCacheFilesConfig.map(async (item) => {
            const file = new File(
              [new Blob([new ArrayBuffer(item.fileObject.contentLength)])],
              item.fileObject.filename
            );

            setFilesState((prev) => ({
              ...prev,
              [item.key]: {
                file,
                error: '',
              },
            }));
          })
        );
      }
      setIsFilesUploaded(true);
    } finally {
      setIsUploading(false);
    }
  };

  const uploadFiles = async () => {
    if (skipUpload) {
      await onComplete();
      return;
    }

    if (isFilesUploaded) {
      await onComplete();
      return;
    }

    if (Object.keys(filesState).length > 0 && !isUploading) {
      setIsUploading(true);

      setFilesState((prev) => {
        const updatedState = { ...prev };
        Object.keys(updatedState).forEach((key) => {
          if (updatedState[key].file) {
            updatedState[key].error = '';
          }
        });
        return updatedState;
      });

      try {
        await Promise.all(
          Object.keys(filesState).map(async (key) => {
            const uploadUrl = config.find(
              (item) => item.key === key
            )?.uploadUrl;
            const file = filesState[key].file;
            if (uploadUrl && file) {
              const result = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
              });
              if (result.status !== 200) {
                throw new Error('Failed to upload file. Please try again!');
              }
            }
          })
        );
        setIsFilesUploaded(true);
        await onComplete();
      } catch (error) {
        setFilesState((prev) => {
          const updatedState = { ...prev };
          Object.keys(updatedState).forEach((key) => {
            if (updatedState[key].file) {
              updatedState[key].error = error.message;
            }
          });
          return updatedState;
        });
        await refetchCredentials();
      } finally {
        setIsUploading(false);
      }
    }
  };

  useEffect(() => {
    const areFilesExist =
      config.map((item) => item.fileUrl).filter(Boolean).length > 0;
    if (areFilesExist) {
      prefetchFiles();
    }
  }, []);

  return children({
    uploadFiles,
    isUploading,
    filesState,
    retryUpload: uploadFiles,
    onFileChange: (key: string, file: File | null) => {
      setFilesState((prev) => ({
        ...prev,
        [key]: {
          file: file,
          error: '',
        },
      }));
      setIsFilesUploaded(false);
    },
  });
}
